import React, { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { Modal } from "react-bootstrap";
import useQueryQueue from "components/hooks/useQueryQueue";
import AuthContext from "store/authContext";
import useTareasUsuario from "components/hooks/useTareasUsuario";
import useAmbitos from "components/hooks/useAmbitos";
import Button from "components/ui/Button/Button";
import Grid from "components/ui/Grid/Grid";
import modalCss from "components/ui/Modal/Modal.module.css";
import Table from "components/ui/Table/Table";
import DateTimePicker from "components/ui/DateTimePicker/DateTimePicker";
import SearchSelectMaterial, {
	includeSearch,
	mapOptions,
} from "components/ui/Select/SearchSelectMaterial";
import Formato from "components/helpers/Formato";
import FormatearFecha from "components/helpers/FormatearFecha";
import UseKeyPress from "components/helpers/UseKeyPress";
import dayjs from "dayjs";

const onCloseDef = () => {};

// Concatena código y nombre, omitiendo vacíos
const formatCodigoNombre = (codigo, nombre) =>
	[codigo, nombre].map(v => String(v || "").trim()).filter(Boolean).join(" - ");

const normalizeText = (value) =>
	String(value ?? "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim();

const formatSeccionalRow = (seccional = {}) =>
	formatCodigoNombre(
		seccional.codigo ?? seccional.codigoSeccional ?? seccional.codSeccional ?? "",
		seccional.descripcion ?? seccional.nombre ?? seccional.label ?? ""
	);

const resolveSeccionalExport = (denuncia, seccionalesData = []) => {
	const seccionalId = Number(denuncia.seccionalId || denuncia.seccional_id || 0) || 0;
	const source = [
		denuncia.seccionalCabecera,
		denuncia.seccional,
		denuncia.seccionalDescripcion,
		denuncia.seccionalCodigo && denuncia.seccionalDescripcion
			? formatCodigoNombre(denuncia.seccionalCodigo, denuncia.seccionalDescripcion)
			: "",
	].find((value) => normalizeText(value));

	let sec = seccionalId
		? seccionalesData.find((s) => Number(s.id) === seccionalId)
		: null;

	if (!sec && source) {
		const target = normalizeText(source);
		sec = seccionalesData.find((s) => {
			const codeName = normalizeText(formatSeccionalRow(s));
			const nameOnly = normalizeText(s.descripcion ?? s.nombre ?? "");
			return codeName === target || nameOnly === target;
		});
	}

	if (sec) {
		return formatSeccionalRow(sec);
	}

	if (source) {
		const sourceText = String(source).trim();
		if (sourceText.includes(" - ")) return sourceText;
		if (denuncia.seccionalCodigo || denuncia.seccionalDescripcion) {
			return formatCodigoNombre(denuncia.seccionalCodigo, denuncia.seccionalDescripcion || sourceText);
		}
		return sourceText;
	}

	return formatSeccionalRow(denuncia) || "";
};

const resolveDerivacionExport = (denuncia, seccionalesData = [], delegacionesData = []) => {
	const tipoDerivacion = (denuncia.derivadoATipo || denuncia.derivadoA_Tipo || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim();
	const idDerivacion = Number(denuncia.derivadoAId || denuncia.derivadoA_Id || 0) || 0;

	if (tipoDerivacion === "seccional") {
		const fallback = denuncia.derivadoSeccional || "";
		let sec = idDerivacion
			? seccionalesData.find((s) => Number(s.id) === idDerivacion)
			: null;

		if (!sec && fallback) {
			const target = normalizeText(fallback);
			sec = seccionalesData.find((s) => normalizeText(formatSeccionalRow(s)) === target);
		}

		if (sec) return formatSeccionalRow(sec);
		if (fallback && String(fallback).includes(" - ")) return String(fallback).trim();
		return fallback ? String(fallback).trim() : "";
	}

	if (tipoDerivacion === "delegacion") {
		const fallback = denuncia.derivadoDelegacion || "";
		let deleg = idDerivacion
			? delegacionesData.find((d) => Number(d.id) === idDerivacion)
			: null;

		if (!deleg && fallback) {
			const target = normalizeText(fallback);
			deleg = delegacionesData.find((d) => {
				const codeName = normalizeText(formatCodigoNombre(d.codigoDelegacion || d.codigo, d.nombre));
				const nameOnly = normalizeText(d.nombre ?? "");
				return codeName === target || nameOnly === target;
			});
		}

		if (deleg) {
			return formatCodigoNombre(deleg.codigoDelegacion || deleg.codigo, deleg.nombre);
		}
		if (fallback && String(fallback).includes(" - ")) return String(fallback).trim();
		return fallback ? String(fallback).trim() : "";
	}

	return "";
};

// Columnas base para la tabla de denuncias
const baseColumns = [
	{
		dataField: "fecha",
		text: "Fecha de carga",
		sort: true,
		headerTitle: () => "Fecha de la denuncia",
		headerStyle: { width: "7em", textAlign: "center" },
		formatter: (v) => FormatearFecha(v),
		csvFormat: (v) => FormatearFecha(v),
		style: { textAlign: "center" },
	},
	{
		dataField: "fechaIngreso",
		text: "Fecha de ingreso",
		sort: false,
		headerTitle: true,
		headerStyle: { width: "7em", textAlign: "center" },
		formatter: (v) => (v ? FormatearFecha(v) : ""),
		csvFormat: (v) => (v ? FormatearFecha(v) : ""),
		style: { textAlign: "center" },
	},
	{
		dataField: "nombre",
		text: "Denunciante",
		sort: true,
		headerTitle: true,
		headerStyle: { width: "10em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "telefono",
		text: "Teléfono",
		headerTitle: true,
		headerStyle: { width: "8em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "center" },
	},
	{
		dataField: "provincia",
		text: "Provincia",
		headerTitle: true,
		headerStyle: { width: "8em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "localidad",
		text: "Localidad",
		headerTitle: true,
		headerStyle: { width: "8em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "seccionalCodigo",
		text: "Código Seccional",
		headerTitle: true,
		headerStyle: { width: "6em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "center" },
	},
	{
		dataField: "seccional",
		text: "Seccional",
		headerTitle: true,
		headerStyle: { width: "9em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "estado",
		text: "Estado",
		headerTitle: true,
		headerStyle: { width: "8em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "center" },
	},
	{
		dataField: "fechaUltimaNovedad",
		text: "Fecha Ultima Novedad",
		headerTitle: true,
		headerStyle: { width: "9em", textAlign: "center" },
		formatter: (v) => v ? FormatearFecha(v) : "Sin fecha",
		csvFormat: (v) => v ? FormatearFecha(v) : "Sin fecha",
		style: { textAlign: "center" },
	},
	{
		dataField: "ultimaNovedad",
		text: "Ultima Novedad",
		headerTitle: true,
		headerStyle: { width: "12em", textAlign: "center" },
		csvFormat: (v) => v || "Sin novedad",
		style: { textAlign: "left" },
		formatter: (v) => (
			<div style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
				{v || "Sin novedad"}
			</div>
		),
	},
	{
		dataField: "empleadorNombre",
		text: "Empresa",
		headerTitle: true,
		headerStyle: { width: "10em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "empleadorCUIT",
		text: "CUIT",
		headerTitle: true,
		headerStyle: { width: "8em", textAlign: "center" },
		formatter: (v) => v ? Formato.Cuit(v) : "",
		csvFormat: (v) => v,
		style: { textAlign: "center" },
	},
	{
		dataField: "derivadoDelegacion",
		text: "Derivado a Delegación",
		headerTitle: true,
		headerStyle: { width: "10em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
	{
		dataField: "derivadoSeccional",
		text: "Derivado a Seccional",
		headerTitle: true,
		headerStyle: { width: "10em", textAlign: "center" },
		csvFormat: (v) => v,
		style: { textAlign: "left" },
	},
];

//#region estadosSelectOptions
const estadosSelectTodos = { value: "", label: "Todos los estados" };
const estadosSelectOptions = ({ data = [], buscar = "", ...x }) =>
	mapOptions({
		data,
		map: (r) => ({ value: r.value, label: r.label }),
		filter: (r) => includeSearch(r, buscar),
		start: [estadosSelectTodos],
		...x,
	});
//#endregion estadosSelectOptions

const ExportModal = ({
	onClose = onCloseDef,
	currentFilters = {},
	usuarioAmbito = null,
	applyAmbitoFilter = null,
	initialData = null,
}) => {
	const { usuario } = useContext(AuthContext);
	const tareasManager = useTareasUsuario();
	const ambitosManager = useAmbitos();
	const ambitoInfoGlobal = useMemo(() => ambitosManager.ambitoUser(), [ambitosManager]);

	// Determina si el usuario puede ver todos los datos
	const puedeVerTodosLosDatos = useMemo(() => {
		try {
			const esAdministrador = usuario?.roles?.includes("Administrador") || false;
			const ambitoName = ambitoInfoGlobal?.tipo || usuario?.ambito || null;
			if (esAdministrador || ambitoName === "Todos") return true;
			return tareasManager.hasTarea("Denuncias_Datos");
		} catch (error) {
			console.error("Error verificando permiso Denuncias_Datos en ExportModal:", error);
			return false;
		}
	}, [usuario, ambitoInfoGlobal, tareasManager]);

	// Estado para el catálogo de situaciones (declarado aquí para usarlo en columns)
	const situacionSelectTodos = useMemo(() => ({ value: null, label: "Todas las situaciones" }), []);
	const situacionAltaOptions = useMemo(() => ([
		{ value: 1, label: "Consultas Salariales" },
		{ value: 2, label: "Reclamos/Diferencias Salariales" },
		{ value: 3, label: "Trabajo NO Registrado" },
		{ value: 4, label: "Maltrato laboral" },
		{ value: 5, label: "Condiciones laborales inaceptables" },
		{ value: 6, label: "Falta de Ropa de Trabajo" },
		{ value: 7, label: "Otras" },
		{ value: 8, label: "Seguridad, higiene y salud en el trabajo." },
		{ value: 9, label: "Condiciones de vivienda, alimentación y traslado." },
		{ value: 10, label: "Indicios de explotación laboral." },
		{ value: 11, label: "Trabajo infantil y adolescente." },
	]), []);
	const situacionInicialId = currentFilters.denunciaSituacionId || null;
	const [situacionSelect, setSituacionSelect] = useState({
		loading: "Cargando...",
		buscar: "",
		data: [],
		error: null,
		options: [],
		selected: { value: null, label: "Todas las situaciones" },
		loaded: false,
	});

	// Columnas dinámicas según permisos
	const columns = useMemo(() => {
		const situacionCatalog = situacionSelect.data.length > 0
			? situacionSelect.data
			: situacionAltaOptions;

		const situacionColumn = {
			dataField: "denunciaSituacionId",
			text: "Situación",
			headerTitle: true,
			headerStyle: { width: "9em", textAlign: "center" },
			formatter: (v) => {
				if (!v) return "";
				return situacionCatalog.find(s => Number(s.id ?? s.value) === Number(v))?.descripcion || situacionCatalog.find(s => Number(s.id ?? s.value) === Number(v))?.label || String(v);
			},
			csvFormat: (v) => {
				if (!v) return "";
				return situacionCatalog.find(s => Number(s.id ?? s.value) === Number(v))?.descripcion || situacionCatalog.find(s => Number(s.id ?? s.value) === Number(v))?.label || String(v);
			},
			style: { textAlign: "left" },
		};
		const idColumn = {
			dataField: "id",
			text: "Nro. Denuncia",
			headerTitle: true,
			headerStyle: { width: "6em", textAlign: "center" },
			csvFormat: (v) => v,
			style: { textAlign: "center" },
		};
		const numeroSeguimientoColumn = {
			dataField: "numeroSeguimiento",
			text: "Nro. Seguimiento",
			headerTitle: true,
			headerStyle: { width: "8em", textAlign: "center" },
			csvFormat: (v) => v,
			style: { textAlign: "center" },
		};
		if (puedeVerTodosLosDatos) {
			return [idColumn, numeroSeguimientoColumn, ...baseColumns, situacionColumn];
		}
		return baseColumns
			.filter((c) => ["fecha", "telefono", "localidad", "estado"].includes(c.dataField))
			.concat(situacionColumn);
	}, [puedeVerTodosLosDatos, situacionSelect.data, situacionAltaOptions]);

	// Verificar permisos para mostrar el modal de exportación
	const tienePermisoExportar = useMemo(() => {
		if (!usuario || !tareasManager) return false;
		try {
			const esAdministrador = usuario?.roles?.includes("Administrador") || false;
			const ambitoName = ambitoInfoGlobal?.tipo || usuario?.ambito || null;
			const tieneTareaExcel = tareasManager.hasTarea("Excel_Denuncias");
			return esAdministrador || ambitoName === "Todos" || tieneTareaExcel;
		} catch (error) {
			console.error("Error verificando permisos en ExportModal:", error);
			return false;
		}
	}, [usuario, tareasManager, ambitoInfoGlobal]);

	//#region Trato queries a APIs
	const pushQuery = useQueryQueue((action) => {
		switch (action) {
			case "GetDenuncias": {
				return {
					config: {
						baseURL: "App",
						endpoint: `/AppDenuncias`,
						method: "GET",
					},
				};
			}
			case "GetDenunciasEstados": {
				return {
					config: {
						baseURL: "App",
						endpoint: `/DenunciasEstados`,
						method: "GET",
					},
				};
			}
			case "GetSeccionales": {
				return {
					config: {
						baseURL: "Afiliaciones",
						endpoint: `/Seccional`,
						method: "GET",
					},
				};
			}
			case "GetDelegaciones": {
				return {
					config: {
						baseURL: "Comunes",
						endpoint: `/RefDelegacion/GetAll`,
						method: "GET",
					},
				};
			}
			case "GetDenunciaSituacion": {
				return {
					config: {
						baseURL: "App",
						endpoint: `/DenunciaSituacion`,
						method: "GET",
					},
				};
			}
			default:
				return null;
		}
	});
	//#endregion

	//#region filtro estados
	const estadoInicialValue = currentFilters.estado || "";
	const [estadoSelect, setEstadoSelect] = useState({
		loading: null,
		buscar: "",
		data: [],
		error: null,
		options: [],
		selected: estadoInicialValue
			? { value: estadoInicialValue, label: estadoInicialValue }
			: estadosSelectTodos,
	});

	useEffect(() => {
		if (estadoSelect.data.length > 0) {
			const options = estadosSelectOptions({
				data: estadoSelect.data,
				buscar: estadoSelect.buscar,
			});
			setEstadoSelect((s) => ({
				...s,
				options,
			}));
		}
	}, [estadoSelect.buscar, estadoSelect.data]);

	//#endregion filtro estados

	//#region filtro situacion
	useEffect(() => {
		if (situacionSelect.loaded) return;
		pushQuery({
			action: "GetDenunciaSituacion",
			params: {},
			onOk: (data) => {
				const arr = Array.isArray(data) ? data : [];
				const merged = [
					...arr,
					...situacionAltaOptions
						.filter((opt) => !arr.some((r) => Number(r.id) === Number(opt.value)))
						.map((opt) => ({ id: opt.value, descripcion: opt.label })),
				];
				const options = [situacionSelectTodos, ...merged.map(r => ({ value: r.id, label: r.descripcion }))];
				const selected = situacionInicialId
					? (options.find(o => Number(o.value) === Number(situacionInicialId)) || situacionSelectTodos)
					: situacionSelectTodos;
				setSituacionSelect(s => ({ ...s, loading: null, data: merged, options, selected, loaded: true }));
			},
			onError: (error) => {
				setSituacionSelect(s => ({
					...s,
					loading: null,
					error: error?.toString(),
					options: [situacionSelectTodos, ...situacionAltaOptions.map(r => ({ value: r.value, label: r.label }))],
					loaded: true,
				}));
			},
		});
	}, [pushQuery, situacionSelect.loaded, situacionInicialId, situacionSelectTodos, situacionAltaOptions]);
	//#endregion filtro situacion

	//#region filtro fechas
	const [fechaDesde, setFechaDesde] = useState(currentFilters.fechaDesde || null);
	const [fechaHasta, setFechaHasta] = useState(currentFilters.fechaHasta || null);

	// Función para aplicar filtro por fechas (similar al patrón del archivo principal)
	const aplicarFiltroFechas = useCallback((data) => {
		if (!fechaDesde && !fechaHasta) {
			return data;
		}

		return data.filter(denuncia => {
			if (!denuncia.fecha) return false;

			const fechaDenuncia = dayjs(denuncia.fecha);
			let cumpleFiltro = true;

			if (fechaDesde) {
				const fechaDesdeFormatted = dayjs(fechaDesde);
				cumpleFiltro = cumpleFiltro && fechaDenuncia.isAfter(fechaDesdeFormatted.subtract(1, 'day'));
			}

			if (fechaHasta) {
				const fechaHastaFormatted = dayjs(fechaHasta);
				cumpleFiltro = cumpleFiltro && fechaDenuncia.isBefore(fechaHastaFormatted.add(1, 'day'));
			}

			return cumpleFiltro;
		});
	}, [fechaDesde, fechaHasta]);
	//#endregion filtro fechas

	//#region estados de denuncias
	const [estadosDenuncias, setEstadosDenuncias] = useState({
		loading: false,
		data: [],
		error: null,
		loaded: false
	});

	// Cargar todos los estados de denuncias una vez
	const cargarEstadosDenuncias = useCallback(() => {
		if (estadosDenuncias.loaded || estadosDenuncias.loading) return;

		setEstadosDenuncias(prev => ({ ...prev, loading: true, error: null }));
		
		pushQuery({
			action: "GetDenunciasEstados",
			params: {},
			onOk: (data) => {
				if (Array.isArray(data)) {
					setEstadosDenuncias({
						loading: false,
						data: data,
						error: null,
						loaded: true
					});
				} else {
					console.error("Se esperaba un arreglo de estados", data);
					setEstadosDenuncias(prev => ({ 
						...prev, 
						loading: false, 
						error: "Formato de datos incorrecto",
						loaded: true
					}));
				}
			},
			onError: (error) => {
				console.error("Error cargando estados de denuncias:", error);
				setEstadosDenuncias(prev => ({ 
					...prev, 
					loading: false, 
					error: error.toString(),
					loaded: true
				}));
			}
		});
	}, [pushQuery, estadosDenuncias.loaded, estadosDenuncias.loading]);

	// Cargar estados de denuncias al montar el componente
	useEffect(() => {
		cargarEstadosDenuncias();
	}, [cargarEstadosDenuncias]);

	// Actualizar opciones de estado cuando se cargan los estados de denuncias
	useEffect(() => {
		if (estadosDenuncias.loaded && estadosDenuncias.data.length > 0) {
			// Extraer estados únicos de los datos cargados
			const estadosUnicos = [...new Set(estadosDenuncias.data.map(item => item.estado))]
				.filter(estado => estado)
				.map(estado => ({ value: estado, label: estado }))
				.sort((a, b) => a.label.localeCompare(b.label));

			setEstadoSelect(prev => ({
				...prev,
				data: estadosUnicos
			}));
		}
	}, [estadosDenuncias.loaded, estadosDenuncias.data]);


	//#endregion

	//#region seccionales
	const [seccionales, setSeccionales] = useState({ loaded: false, data: [] });

	useEffect(() => {
		if (seccionales.loaded) return;
		pushQuery({
			action: "GetSeccionales",
			params: { soloActivos: false },
			onOk: (data) => {
				const arr = Array.isArray(data) ? data : data?.data || [];
				setSeccionales({ loaded: true, data: arr });
			},
			onError: () => setSeccionales({ loaded: true, data: [] }),
		});
	}, [pushQuery, seccionales.loaded]);
	//#endregion seccionales

	//#region delegaciones
	const [delegaciones, setDelegaciones] = useState({ loaded: false, data: [] });

	useEffect(() => {
		if (delegaciones.loaded) return;
		pushQuery({
			action: "GetDelegaciones",
			params: { soloActivos: false },
			onOk: (data) => {
				const arr = Array.isArray(data) ? data : data?.data || [];
				setDelegaciones({ loaded: true, data: arr });
			},
			onError: () => setDelegaciones({ loaded: true, data: [] }),
		});
	}, [pushQuery, delegaciones.loaded]);
	//#endregion delegaciones

	// Enriquece un registro con info de seccional y derivaciones
	const enrichWithSeccional = useCallback((denuncia) => {
		const enriched = { ...denuncia };

		// --- Seccional de la denuncia (localización) ---
		if (seccionales.data.length > 0) {
			// Intento 1: por seccionalId si existe
			const seccionalId = denuncia.seccionalId || denuncia.seccional_id;
			let sec = seccionalId
				? seccionales.data.find(s => String(s.id) === String(seccionalId))
				: null;
			// Intento 2: por nombre si hay nombre pero no código
			if (!sec && enriched.seccional && !enriched.seccionalCodigo) {
				const nombreNorm = String(enriched.seccional).toLowerCase().trim();
				sec = seccionales.data.find(s =>
					String(s.descripcion || "").toLowerCase().trim() === nombreNorm
				);
			}
			if (sec) {
				enriched.seccional = enriched.seccional || sec.descripcion || "";
				enriched.seccionalCodigo = enriched.seccionalCodigo || sec.codigo || "";
			}
		}

		// --- Campos de derivación ---
		const tipo = (denuncia.derivadoATipo || denuncia.derivadoA_Tipo || "")
			.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
		const idDerivacion = denuncia.derivadoAId || denuncia.derivadoA_Id;

		if (tipo === "seccional") {
			let val = "";
			if (idDerivacion) {
				const sec = seccionales.data.find(s => String(s.id) === String(idDerivacion));
				if (sec) val = formatCodigoNombre(sec.codigo, sec.descripcion);
			}
			if (!val && enriched.derivadoSeccional) {
				const nomSec = String(enriched.derivadoSeccional).toLowerCase().trim();
				const sec = seccionales.data.find(s =>
					String(s.descripcion || "").toLowerCase().trim() === nomSec
				);
				val = sec
					? formatCodigoNombre(sec.codigo, sec.descripcion)
					: enriched.derivadoSeccional;
			}
			enriched.derivadoSeccional = val || enriched.derivadoSeccional || "";
			enriched.derivadoDelegacion = enriched.derivadoDelegacion || "";
		} else if (tipo === "delegacion") {
			let val = "";
			if (idDerivacion) {
				const deleg = delegaciones.data.find(d => String(d.id) === String(idDerivacion));
				if (deleg) val = formatCodigoNombre(deleg.codigoDelegacion || deleg.codigo, deleg.nombre);
			}
			if (!val && enriched.derivadoDelegacion) {
				const nomDeleg = String(enriched.derivadoDelegacion).toLowerCase().trim();
				const deleg = delegaciones.data.find(d =>
					String(d.nombre || "").toLowerCase().trim() === nomDeleg
				);
				val = deleg
					? formatCodigoNombre(deleg.codigoDelegacion || deleg.codigo, deleg.nombre)
					: enriched.derivadoDelegacion;
			}
			enriched.derivadoDelegacion = val || enriched.derivadoDelegacion || "";
			enriched.derivadoSeccional = enriched.derivadoSeccional || "";
		} else {
			enriched.derivadoDelegacion = enriched.derivadoDelegacion || "";
			enriched.derivadoSeccional = enriched.derivadoSeccional || "";
		}

		return enriched;
	}, [seccionales.data, delegaciones.data]);

	//#region list denuncias
	const [list, setList] = useState({
		reload: initialData ? false : true,
		loading: null,
		pagination: { index: 1, size: 100 },
		sort: "+fecha",
		params: { ...currentFilters },
		data: initialData || [],
		selected: [],
		error: null,
	});

	// Cuando se pasan datos pre-cargados (initialData), aplicar filtros client-side
	useEffect(() => {
		if (!initialData) return;
		let data = [...initialData];

		if (estadoSelect.selected?.value) {
			data = data.filter((d) => d.estado === estadoSelect.selected.value);
		}

		if (situacionSelect.selected?.value != null) {
			data = data.filter((d) => Number(d.denunciaSituacionId) === Number(situacionSelect.selected.value));
		}

		data = aplicarFiltroFechas(data);

		data = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

		setList((prev) => ({ ...prev, data }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData, estadoSelect.selected, situacionSelect.selected, aplicarFiltroFechas]);

	useEffect(() => {
		if (!list.reload) return;

		// Esperar a que se carguen los catálogos antes de cargar las denuncias
		if (!estadosDenuncias.loaded || !seccionales.loaded || !delegaciones.loaded) {
			return;
		}
		
		const changes = {
			reload: false,
			loading: "Cargando denuncias...",
			data: [],
			error: null,
		};
		setList((o) => ({ ...o, ...changes }));

		// Excluir estado de los params de la API: el backend no filtra por estado como string
		// y para ciertos valores ("En Planificacion", "Gestion con Empleador") retorna error.
		// El filtro de estado se aplica exclusivamente client-side.
		const { estado: _estadoIgnored, pageSize: _psIgnored, pageIndex: _piIgnored, sortBy: _sbIgnored, ...apiBaseParams } = list.params;
		let params = {
			...apiBaseParams,
			sortBy: list.sort,
			pageIndex: 1,
			pageSize: 10000,
		};

		pushQuery({
			action: "GetDenuncias",
			params: params,
			onOk: async (response) => {
				try {
					// Normalizar respuesta
					let data = [];
					let pagination = {};
					
					if (response && typeof response === "object") {
						data = response.data || [];
						pagination = {
							index: response.index || 1,
							size: response.size || 10,
							count: response.count || 0,
							pages: response.pages || 1,
						};
					} else if (Array.isArray(response)) {
						data = response;
						pagination = { index: 1, size: data.length, count: data.length, pages: 1 };
					}

					if (!Array.isArray(data)) {
						console.error("Se esperaba un arreglo", response);
						data = [];
					}

					// Aplicar filtro por ámbito si corresponde
					if (usuarioAmbito && applyAmbitoFilter) {
						try {
							data = await applyAmbitoFilter(data, usuarioAmbito, pushQuery);
						} catch (error) {
							console.warn("Error aplicando filtro de ámbito:", error);
						}
					}

					// Enriquecer datos con el estado, fecha y observaciones de cada denuncia
					if (estadosDenuncias.data.length > 0) {
						data = data.map(denuncia => {
							// Buscar todos los estados de esta denuncia (Number() evita mismatch string/number)
							const estadosDeDenuncia = estadosDenuncias.data.filter(
								estado => Number(estado.appDenunciasId) === Number(denuncia.id)
							);
							
							let estadoActual = "Sin estado";
							let fechaUltimaNovedad = null;
							let ultimaNovedad = "Sin novedad";
							
							if (estadosDeDenuncia.length > 0) {
								// Ordenar por fecha (más reciente primero) y tomar el primero
								const ultimoEstado = estadosDeDenuncia.sort((a, b) => 
									new Date(b.fecha || b.fechaAsociada) - new Date(a.fecha || a.fechaAsociada)
								)[0];
								
								estadoActual = ultimoEstado.estado || "Sin estado";
								fechaUltimaNovedad = ultimoEstado.fecha || ultimoEstado.fechaAsociada;
								ultimaNovedad = ultimoEstado.observaciones || "Sin observaciones";
							}
							
							return {
								...denuncia,
								estado: estadoActual,
								fechaUltimaNovedad: fechaUltimaNovedad,
								ultimaNovedad: ultimaNovedad
							};
						});
					}

					// Enriquecer con seccional y nombres de derivación
					data = data.map((d) => enrichWithSeccional(d));

					// Aplicar filtro por estado si está seleccionado (cadena vacía = todos)
					if (estadoSelect.selected && estadoSelect.selected.value && estadoSelect.selected.value !== "") {
						data = data.filter(denuncia => denuncia.estado === estadoSelect.selected.value);
					}

					// Aplicar filtro por fechas
					data = aplicarFiltroFechas(data);

					// Aplicar ordenamiento por fecha (más reciente primero)
					data = data.sort((a, b) => {
						const fechaA = new Date(a.fecha);
						const fechaB = new Date(b.fecha);
						return fechaB - fechaA; // Orden descendente (más reciente primero)
						});

					changes.data = data;
					changes.pagination = pagination;
				} catch (error) {
					console.error("Error procesando respuesta:", error);
					changes.error = error.toString();
				}
			},
			onError: async (error) => (changes.error = error.toString()),
			onFinally: async () =>
				setList((o) => ({ ...o, ...changes, loading: null })),
		});
	}, [list, pushQuery, usuarioAmbito, applyAmbitoFilter, estadoSelect.selected, estadosDenuncias.data, estadosDenuncias.loaded, aplicarFiltroFechas, seccionales.loaded, delegaciones.loaded, enrichWithSeccional]);

	// Enriquecer denuncias existentes cuando se cargan los estados por primera vez
	useEffect(() => {
		if (estadosDenuncias.loaded && estadosDenuncias.data.length > 0 && list.data.length > 0) {
			// Verificar si las denuncias ya tienen todos los datos de estados
			const denunciasSinDatos = list.data.filter(d => 
				!d.estado || d.estado === "" || d.estado === "Sin estado" ||
				!d.fechaUltimaNovedad || !d.ultimaNovedad
			);
			
			if (denunciasSinDatos.length > 0) {
				// Enriquecer datos existentes con estados, fecha, observaciones y seccional
				const datosEnriquecidos = list.data.map(denuncia => {
					if (denuncia.estado && denuncia.estado !== "" && denuncia.estado !== "Sin estado" 
						&& denuncia.fechaUltimaNovedad && denuncia.ultimaNovedad) {
						// Ya tiene todos los datos, no cambiar
						return denuncia;
					}
					
					// Buscar todos los estados de esta denuncia (Number() evita mismatch string/number)
					const estadosDeDenuncia = estadosDenuncias.data.filter(
						estado => Number(estado.appDenunciasId) === Number(denuncia.id)
					);

					let estadoActual = "Sin estado";
					let fechaUltimaNovedad = null;
					let ultimaNovedad = "Sin novedad";

					if (estadosDeDenuncia.length > 0) {
						// Ordenar por fecha (más reciente primero) y tomar el primero
						const ultimoEstado = estadosDeDenuncia.sort((a, b) =>
							new Date(b.fecha || b.fechaAsociada) - new Date(a.fecha || a.fechaAsociada)
						)[0];
						
						estadoActual = ultimoEstado.estado || "Sin estado";
						fechaUltimaNovedad = ultimoEstado.fecha || ultimoEstado.fechaAsociada;
						ultimaNovedad = ultimoEstado.observaciones || "Sin observaciones";
					}
					
					return {
						...denuncia,
						estado: estadoActual,
						fechaUltimaNovedad: fechaUltimaNovedad,
						ultimaNovedad: ultimaNovedad
					};
				});
				
				// Aplicar enrichWithSeccional sobre cada registro
				const datosConSeccional = datosEnriquecidos.map((d) => enrichWithSeccional(d));

				// Aplicar filtro por estado si está seleccionado
				let datosFiltrados = datosConSeccional;
				if (estadoSelect.selected && estadoSelect.selected.value && estadoSelect.selected.value !== "") {
					datosFiltrados = datosConSeccional.filter(denuncia => denuncia.estado === estadoSelect.selected.value);
				}

				// Aplicar filtro por fechas
				datosFiltrados = aplicarFiltroFechas(datosFiltrados);

				// Aplicar ordenamiento por fecha (más reciente primero)
				datosFiltrados = datosFiltrados.sort((a, b) => {
					const fechaA = new Date(a.fecha);
					const fechaB = new Date(b.fecha);
					return fechaB - fechaA; // Orden descendente (más reciente primero)
				});
				
				setList(prev => ({ ...prev, data: datosFiltrados }));
			}
		}
	}, [estadosDenuncias.loaded, estadosDenuncias.data, list.data, estadoSelect.selected, aplicarFiltroFechas, enrichWithSeccional]);
	//#endregion

	//#region nueva seleccion
	const [newSelection, setNewSelection] = useState({
		reload: false,
		loading: null,
		params: {},
		error: null,
	});

	useEffect(() => {
		if (!newSelection.reload) return;
		const changes = {
			reload: false,
			loading: "Cargando bloque 1...",
			error: null,
		};
		
		let params = {
			...newSelection.params,
			pageIndex: 1,
			pageSize: 1000, // Obtener muchos registros por bloque
		};

		// Aplicar filtro por estado si está seleccionado
		if (estadoSelect.selected && estadoSelect.selected.value) {
			params.estado = estadoSelect.selected.value;
		}

		const query = {
			action: "GetDenuncias",
			params: params,
		};

		query.onOk = async (response) => {
			try {
				let data = [];
				let currentIndex = 1;
				let totalPages = 1;
				
				if (response && typeof response === "object") {
					data = response.data || [];
					currentIndex = response.index || 1;
					totalPages = response.pages || 1;
				} else if (Array.isArray(response)) {
					data = response;
				}

				if (Array.isArray(data)) {
					// Aplicar filtro por ámbito si corresponde
					if (usuarioAmbito && applyAmbitoFilter) {
						try {
							data = await applyAmbitoFilter(data, usuarioAmbito, pushQuery);
						} catch (error) {
							console.warn("Error aplicando filtro de ámbito:", error);
						}
					}

					setList((o) => ({
						...o,
						selected: [...o.selected, ...data].filter(
							(v, i, a) => a.findIndex(r => r.id === v.id) === i
						),
					}));
				} else {
					console.error("Se esperaba un arreglo", response);
				}

				if (currentIndex < totalPages) {
					changes.loading = `Cargando bloque ${currentIndex + 1} de ${totalPages}...`;
					query.params = {
						...newSelection.params,
						pageIndex: currentIndex + 1,
						pageSize: 1000,
					};
					pushQuery({ ...query });
				} else {
					changes.loading = null;
				}
			} catch (error) {
				console.error("Error en selección masiva:", error);
				changes.loading = null;
				changes.error = error.toString();
			}
		};

		query.onError = async (error) => {
			changes.loading = null;
			changes.error = error.toString();
		};

		query.onFinally = async () =>
			setNewSelection((o) => ({ ...o, ...changes }));

		setNewSelection((o) => ({ ...o, ...changes }));
		pushQuery(query);
	}, [newSelection, pushQuery, usuarioAmbito, applyAmbitoFilter, estadoSelect.selected]);
	//#endregion nueva seleccion

	// Estado para el progreso de carga de novedades
	const [loadingNovedades, setLoadingNovedades] = useState(false);

	// Función para exportar con últimas novedades
	const onExport = () => {
		if (list.selected.length === 0) {
			alert("Debe seleccionar al menos un registro para exportar.");
			return;
		}

		if (estadosDenuncias.loading) {
			alert("Espere a que se carguen los estados de las denuncias...");
			return;
		}

		if (estadosDenuncias.error) {
			alert(`Error cargando estados: ${estadosDenuncias.error}. ¿Desea continuar sin las novedades?`);
		}

		setLoadingNovedades(`Procesando ${list.selected.length} denuncias...`);

		try {
			const exportData = list.selected.map((denuncia) => {
				const fechaUltimaNovedadFormatted = denuncia.fechaUltimaNovedad ? FormatearFecha(denuncia.fechaUltimaNovedad) : "Sin fecha";
				const seccionalStrResolved = resolveSeccionalExport(denuncia, seccionales.data);
				const derivadoDelegacionStr = resolveDerivacionExport(
					{ ...denuncia, derivadoATipo: "Delegacion", derivadoDelegacion: denuncia.derivadoDelegacion || "" },
					seccionales.data,
					delegaciones.data
				);
				const derivadoSeccionalStr = resolveDerivacionExport(
					{ ...denuncia, derivadoATipo: "Seccional", derivadoSeccional: denuncia.derivadoSeccional || "" },
					seccionales.data,
					delegaciones.data
				);

				// ── Seccional (localización de la denuncia) ──────────────────────────────
				let seccionalStr = "";
				if (seccionales.data.length > 0) {
					const seccionalId = Number(denuncia.seccionalId || denuncia.seccional_id || 0) || 0;
					let sec = seccionalId
						? seccionales.data.find(s => Number(s.id) === seccionalId)
						: null;
					// Fallback: buscar por nombre si no hay ID
					if (!sec && denuncia.seccional) {
						const nameLower = String(denuncia.seccional).toLowerCase().trim();
						sec = seccionales.data.find(s =>
							String(s.descripcion || "").toLowerCase().trim() === nameLower
						);
					}
					seccionalStr = sec
						? formatCodigoNombre(sec.codigo, sec.descripcion)
						: (denuncia.seccional || "");
				} else {
					seccionalStr = denuncia.seccional || "";
				}

				// ── Derivación ───────────────────────────────────────────────────────────
				const tipoDerivacion = (denuncia.derivadoATipo || denuncia.derivadoA_Tipo || "")
					.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
				const idDerivacion = Number(denuncia.derivadoAId || denuncia.derivadoA_Id || 0) || 0;

				let derivadoDelegacion = "";
				let derivadoSeccional = "";

				if (tipoDerivacion === "seccional") {
					// Intento 1: por ID
					if (idDerivacion) {
						const sec = seccionales.data.find(s => Number(s.id) === idDerivacion);
						if (sec) derivadoSeccional = formatCodigoNombre(sec.codigo, sec.descripcion);
					}
					// Intento 2: por nombre si el ID no encontró nada
					if (!derivadoSeccional && denuncia.derivadoSeccional) {
						const nomSec = String(denuncia.derivadoSeccional).toLowerCase().trim();
						const sec = seccionales.data.find(s =>
							String(s.descripcion || "").toLowerCase().trim() === nomSec
						);
						derivadoSeccional = sec
							? formatCodigoNombre(sec.codigo, sec.descripcion)
							: (denuncia.derivadoSeccional || "");
					}
					derivadoDelegacion = denuncia.derivadoDelegacion || "";
				} else if (tipoDerivacion === "delegacion") {
					// Intento 1: por ID
					if (idDerivacion) {
						const deleg = delegaciones.data.find(d => Number(d.id) === idDerivacion);
						if (deleg) derivadoDelegacion = formatCodigoNombre(deleg.codigoDelegacion || deleg.codigo, deleg.nombre);
					}
					// Intento 2: por nombre si el ID no encontró nada
					if (!derivadoDelegacion && denuncia.derivadoDelegacion) {
						const nomDeleg = String(denuncia.derivadoDelegacion).toLowerCase().trim();
						const deleg = delegaciones.data.find(d =>
							String(d.nombre || "").toLowerCase().trim() === nomDeleg
						);
						derivadoDelegacion = deleg
							? formatCodigoNombre(deleg.codigoDelegacion || deleg.codigo, deleg.nombre)
							: (denuncia.derivadoDelegacion || "");
					}
					derivadoSeccional = denuncia.derivadoSeccional || "";
				} else {
					derivadoDelegacion = denuncia.derivadoDelegacion || "";
					derivadoSeccional = denuncia.derivadoSeccional || "";
				}

				// ── Armar fila ───────────────────────────────────────────────────────────
				const item = {};
				if (puedeVerTodosLosDatos) item["Nro. Denuncia"] = denuncia.id;
				item["Nro. Seguimiento"] = denuncia.numeroSeguimiento || "";
				item["Fecha de carga"] = denuncia.fecha ? FormatearFecha(denuncia.fecha) : "";
				item["Fecha de ingreso"] = denuncia.fechaIngreso ? FormatearFecha(denuncia.fechaIngreso) : "";
				item["Denunciante"] = denuncia.nombre || "";
				item["Correo"] = denuncia.correo || "";
				item["Teléfono"] = denuncia.telefono || denuncia.telefonoContacto || "";
				item["Provincia"] = denuncia.provincia || "";
				item["Localidad"] = denuncia.localidad || "";
				item["Seccional"] = seccionalStrResolved;
				item["Estado"] = denuncia.estado || "Sin estado";
				item["Situación"] = denuncia.denunciaSituacionId
					? (situacionSelect.data.find(s => Number(s.id) === Number(denuncia.denunciaSituacionId))?.descripcion
						|| situacionSelect.data.find(s => Number(s.id) === Number(denuncia.denunciaSituacionId))?.label
						|| String(denuncia.denunciaSituacionId))
					: "";
				item["Fecha Ultima Novedad"] = fechaUltimaNovedadFormatted;
				item["Ultima Novedad"] = denuncia.ultimaNovedad || "Sin novedad";
				item["Empresa"] = denuncia.empleadorNombre || "";
				item["CUIT"] = denuncia.empleadorCUIT ? Formato.Cuit(denuncia.empleadorCUIT) : "";
				item["Derivado a Delegación"] = derivadoDelegacionStr;
				item["Derivado a Seccional"] = derivadoSeccionalStr;
				item["Ubicación"] = denuncia.ubicacion || "";
				item["Detalle de la Denuncia"] = denuncia.texto || "";
				return item;
			});
			
			// Cerrar modal y proceder con la exportación
			onClose(exportData, estadoSelect.selected);
		} catch (error) {
			console.error("Error procesando datos:", error);
			alert("Error al procesar los datos para exportación.");
		} finally {
			setLoadingNovedades(false);
		}
	};

	UseKeyPress(["Escape"], () => onClose());
	UseKeyPress(["Enter"], () => onExport(), "AltKey");

	// Si no hay usuario o contexto aún, mostrar loading
	if (!usuario) {
		return (
			<Modal size="xl" centered show>
				<Modal.Header className={modalCss.modalCabecera} closeButton onClick={onClose}>
					Exportar Denuncias
				</Modal.Header>
				<Modal.Body>
					<div style={{ textAlign: "center", padding: "20px" }}>
						Cargando permisos...
					</div>
				</Modal.Body>
			</Modal>
		);
	}

	// Si no tiene permisos, mostrar mensaje
	if (!tienePermisoExportar) {
		return (
			<Modal size="xl" centered show>
				<Modal.Header className={modalCss.modalCabecera} closeButton onClick={onClose}>
					Acceso Denegado
				</Modal.Header>
				<Modal.Body>
					<div style={{ textAlign: "center", padding: "20px", color: "red" }}>
						No tiene permisos para exportar denuncias.
						<br />
						Necesita ser Administrador o tener la tarea "Excel_Denuncias".
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button className="botonAmarillo" onClick={() => onClose()}>
						CERRAR
					</Button>
				</Modal.Footer>
			</Modal>
		);
	}

	return (
		<Modal size="xl" centered show>
			<Modal.Header className={modalCss.modalCabecera} closeButton onClick={onClose}>
				Exportar Denuncias con Última Novedad
			</Modal.Header>
			<Modal.Body>
				<Grid col full gap="15px">
					<Grid width gap="inherit">
						<SearchSelectMaterial
							id="estadoSelect"
							label="Filtro por Estado"
							error={!!estadoSelect.error}
							helperText={
								estadoSelect.loading ?? estadoSelect?.error
							}
							value={estadoSelect.selected}
							onChange={(selected) => {
								setEstadoSelect((o) => ({ ...o, selected }));
								if (initialData) {
									// Filtrado client-side sobre datos pre-cargados
									let data = [...initialData];
									if (selected?.value) {
										data = data.filter((d) => d.estado === selected.value);
									}
									if (situacionSelect.selected?.value != null) {
										data = data.filter((d) => Number(d.denunciaSituacionId) === Number(situacionSelect.selected.value));
									}
									data = aplicarFiltroFechas(data);
									data = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
									setList((o) => ({ ...o, data, selected: [] }));
								} else {
									setList((o) => ({
										...o,
										reload: true,
										data: [],
										selected: [],
										pagination: { ...o.pagination, index: 1 },
									}));
								}
							}}
							options={estadoSelect.options}
							onTextChange={(buscar) =>
								setEstadoSelect((o) => ({ ...o, buscar }))
							}
						/>
						<SearchSelectMaterial
							id="situacionSelect"
							label="Filtro por Situación"
							error={!!situacionSelect.error}
							helperText={situacionSelect.loading ?? situacionSelect?.error}
							value={situacionSelect.selected}
							onChange={(selected) => {
								setSituacionSelect((o) => ({ ...o, selected: selected || situacionSelectTodos }));
								if (initialData) {
									let data = [...initialData];
									if (estadoSelect.selected?.value) {
										data = data.filter((d) => d.estado === estadoSelect.selected.value);
									}
									if (selected?.value != null) {
										data = data.filter((d) => Number(d.denunciaSituacionId) === Number(selected.value));
									}
									data = aplicarFiltroFechas(data);
									data = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
									setList((o) => ({ ...o, data, selected: [] }));
								} else {
									setList((o) => ({
										...o,
										reload: true,
										data: [],
										selected: [],
										pagination: { ...o.pagination, index: 1 },
										params: {
											...o.params,
											denunciaSituacionId: selected?.value ? Number(selected.value) : undefined,
										},
									}));
								}
							}}
							options={situacionSelect.options}
							onTextChange={(buscar) =>
								setSituacionSelect((o) => ({ ...o, buscar }))
							}
						/>
					</Grid>

					{/* Filtros de fecha */}
					<Grid width gap="10px">
						<DateTimePicker
							type="date"
							label="Fecha Desde"
							value={fechaDesde}
							onChange={(value) => {
								setFechaDesde(value);
								if (initialData) {
									// Filtrado client-side — aplicarFiltroFechas usará el nuevo valor en el efecto
								} else {
									setList((prev) => ({
										...prev,
										reload: true,
										data: [],
										selected: [],
										params: {
											...prev.params,
											...(value ? { fechaDesde: dayjs(value).format("YYYY-MM-DD") } : { fechaDesde: undefined }),
										},
									}));
								}
							}}
							format="YYYY-MM-DD"
						/>

						<DateTimePicker
							type="date"
							label="Fecha Hasta"
							value={fechaHasta}
							onChange={(value) => {
								setFechaHasta(value);
								if (initialData) {
									// Filtrado client-side — aplicarFiltroFechas usará el nuevo valor en el efecto
								} else {
									setList((prev) => ({
										...prev,
										reload: true,
										data: [],
										selected: [],
										params: {
											...prev.params,
											...(value ? { fechaHasta: dayjs(value).format("YYYY-MM-DD") } : { fechaHasta: undefined }),
										},
									}));
								}
							}}
							format="YYYY-MM-DD"
						/>

						<Button
							className="botonAzul"
							disabled={!fechaDesde && !fechaHasta}
							onClick={() => {
								setFechaDesde(null);
								setFechaHasta(null);
								if (!initialData) {
									setList((prev) => ({
										...prev,
										reload: true,
										data: [],
										selected: [],
										params: {
											...prev.params,
											fechaDesde: undefined,
											fechaHasta: undefined,
										},
									}));
								}
							}}
						>
							Limpiar Fechas
						</Button>
					</Grid>
					
					<Table
						keyField="id"
						data={list.data}
						mostrarBuscar={false}
						baseProps={{
							style: { textAlign: "center", overflowX: "scroll" },
						}}
						defaultSorted={[
							{
								dataField: "fecha",
								order: "desc"
							}
						]}
						pagination={{
							page: 1,
							sizePerPage: 10,
							totalSize: list.data.length,
							showTotal: true,
							sizePerPageList: [
								{ text: '10', value: 10 },
								{ text: '25', value: 25 },
								{ text: '50', value: 50 },
								{ text: '100', value: 100 }
							]
						}}
						noDataIndication={
							list.loading || list.error || "No existen datos para mostrar"
						}
						selection={{
							mode: "checkbox",
							hideSelectColumn: false,
							selected: list.selected.map((r) => r.id),
							onSelect: (row, isSelect) => {
								if (!isSelect === !list.selected.find((r) => r.id === row.id))
									return;
								setList((o) => {
									const selected = o.selected.filter((r) => r.id !== row.id);
									if (isSelect) selected.push(row);
									return { ...o, selected };
								});
							},
							onSelectAll: (isSelect, rows) =>
								setList((o) => {
									const selected = o.selected.filter(
										(s) => !rows.find((r) => r.id === s.id)
									);
									if (isSelect) selected.push(...rows);
									return { ...o, selected };
								}),
						}}
						columns={columns}

					/>
				</Grid>
			</Modal.Body>
			<Modal.Footer>
				<Grid width col gap="5px">
					<Grid width gap="20px">
						<Grid width="200px">
							<Button
								className="botonAmarillo"
								onClick={() => {
									// Seleccionar todos los datos ya cargados y filtrados en list.data
									// (ya tienen estado enriquecido y filtros client-side aplicados)
									setList((o) => ({ ...o, selected: [...o.data] }));
								}}
							>
								SELECCIONA TODO
							</Button>
						</Grid>
						<Grid width="200px">
							<Button
								className="botonAmarillo"
								disabled={list.selected.length === 0}
								onClick={() => setList((o) => ({ ...o, selected: [] }))}
							>
								LIMPIA SELECCION
							</Button>
						</Grid>
						<Grid col grow>
							<Grid justify="center">
								Denuncias seleccionadas: {list.selected.length}
								{estadoSelect.selected?.value && (
									<span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>
										(Estado: {estadoSelect.selected.label})
									</span>
								)}
							</Grid>
							{(newSelection.loading || newSelection.error || loadingNovedades || estadosDenuncias.loading || estadosDenuncias.error) && (
								<Grid justify="center" style={{ fontSize: "0.9em", marginTop: "5px" }}>
									{newSelection.loading && (
										<span style={{ color: "green" }}>{newSelection.loading}</span>
									)}
									{newSelection.error && (
										<span style={{ color: "red" }}>Error: {newSelection.error}</span>
									)}
									{loadingNovedades && (
										<span style={{ color: "blue" }}>{loadingNovedades}</span>
									)}
									{estadosDenuncias.loading && (
										<span style={{ color: "orange" }}>Cargando estados...</span>
									)}
									{estadosDenuncias.error && (
										<span style={{ color: "red" }}>Error estados: {estadosDenuncias.error}</span>
									)}
								</Grid>
							)}
						</Grid>
						<Grid width="150px">
							<Button
								className="botonAmarillo"
								disabled={list.selected.length === 0 || loadingNovedades}
								onClick={() => onExport()}
							>
								{loadingNovedades ? "PROCESANDO..." : "EXPORTAR"}
							</Button>
						</Grid>
						<Grid width="150px">
							<Button className="botonAmarillo" onClick={() => onClose()}>
								CANCELAR
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Modal.Footer>
		</Modal>
	);
};

export default ExportModal;